import { cx } from "@emotion/css";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { Draggable } from "react-beautiful-dnd";
import { CopyIcon } from "src/components/AssembleIcons/Brand/CopyIcon";
import { DragHandleIcon } from "src/components/AssembleIcons/Brand/DragHandleIcon";
import { EditIcon } from "src/components/AssembleIcons/Brand/EditIcon";
import makeStyles from "../../../makeStylesV4Compat";
import { ComputedLocationAdjustment } from "../../../models/ComputedLocationAdjustment";
import { useDeleteLocationAdjustment } from "../../../mutations/Location";
import { GRAY_4, GRAY_6, PURPLE_1, PURPLE_2, WHITE } from "../../../theme";
import { getAdjustmentColor } from "../../../utils";
import { AssembleTypography } from "../../AssembleTypography";
import { DeleteButton } from "../../Form/DeleteButton";
import { OrdinalCircle } from "../Benefits/Editor/OrdinalCircle";
import { AdjustmentEditorState } from "./LocationAdjustmentEditor";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
    borderTop: `1px solid ${GRAY_6}`,
    background: WHITE,
    "&:hover .location-adjustment-row-actions, &:focus-within .location-adjustment-row-actions":
      {
        opacity: 1,
      },
    "&:hover .location-adjustment-row-margin-ordinal, &:focus-within .location-adjustment-row-margin-ordinal, &.location-adjustment-row-dragging .location-adjustment-row-margin-ordinal":
      {
        display: "none",
      },
    "&:hover .location-adjustment-row-margin-drag-handle, &:focus-within .location-adjustment-row-margin-drag-handle, &.location-adjustment-row-dragging .location-adjustment-row-margin-drag-handle":
      {
        opacity: 1,
      },
  },
  dragging: {},
  margin: {
    paddingLeft: theme.spacing(1),
    position: "relative",
    width: "1.5rem",
    height: "1.5rem",
  },
  marginOrdinal: {},

  marginDragHandle: {
    position: "absolute",
    top: 0,
    left: theme.spacing(0.5),
    height: "1.5rem",
    width: "1.5rem",
    opacity: 0,
    color: GRAY_4,

    "&:focus-within": {
      color: PURPLE_1,
    },
  },
  adjustmentValue: {
    fontSize: "20px",
  },
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginRight: theme.spacing(1),
    opacity: 0,
    transition: "opacity 300ms",
  },
}));

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: React.CSSProperties = {}
): React.CSSProperties => ({
  // change style if dragging
  border: isDragging ? `1px solid ${GRAY_4}` : undefined,
  boxSizing: isDragging ? "border-box" : undefined,
  boxShadow: isDragging ? "0px 5px 15px rgba(10, 36, 64, 0.13)" : undefined,

  // styles we need to apply on draggables
  ...draggableStyle,
});

type LocationAdjustmentRowProps = {
  locationAdjustment: ComputedLocationAdjustment;
  index: number;
  openAdjustmentEditor: (
    adjustmentEditorState: AdjustmentEditorState
  ) => unknown;
};

export function LocationAdjustmentRow({
  locationAdjustment,
  index,
  openAdjustmentEditor,
}: LocationAdjustmentRowProps): JSX.Element {
  const classes = useStyles();

  const { id, adjustmentValue, departments, ladders, levels, compComponents } =
    locationAdjustment;

  const deleteLocationAdjustment = useDeleteLocationAdjustment(id);

  return (
    <Draggable key={id} draggableId={id.toString()} index={index}>
      {(provided, snapshot) => (
        <Grid
          container
          className={cx(
            classes.container,
            "location-adjustment-row-container",
            {
              [classes.dragging]: snapshot.isDragging,
              "location-adjustment-row-dragging": snapshot.isDragging,
            }
          )}
          ref={(el: HTMLElement | null) => {
            provided.innerRef(el);
          }}
          {...provided.draggableProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style
          )}
          alignItems="center"
        >
          <Grid size={2}>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <div className={classes.margin}>
                <OrdinalCircle
                  className={cx(
                    classes.marginOrdinal,
                    "location-adjustment-row-margin-ordinal"
                  )}
                  ordinal={index + 1}
                />
                <div
                  className={cx(
                    classes.marginDragHandle,
                    "location-adjustment-row-margin-drag-handle"
                  )}
                  {...provided.dragHandleProps}
                >
                  <DragHandleIcon
                    color={GRAY_4}
                    hoverColor={PURPLE_2}
                    width="24px"
                    height="24px"
                  />
                </div>
              </div>
              <Box mr={3} />
              <AssembleTypography
                variant="productTableContent"
                className={classes.adjustmentValue}
                style={{ color: getAdjustmentColor(adjustmentValue) }}
              >
                {formatAdjustmentValue(adjustmentValue)}
              </AssembleTypography>
            </Box>
          </Grid>
          <Grid size={2}>
            <AssembleTypography variant="productTableContent">
              {formatCondition(
                departments.map((d) => d.name),
                "Departments"
              )}
            </AssembleTypography>
          </Grid>
          <Grid size={2}>
            <AssembleTypography variant="productTableContent">
              {formatCondition(
                ladders.map((l) => l.name),
                "Ladders"
              )}
            </AssembleTypography>
          </Grid>
          <Grid size={2}>
            <AssembleTypography variant="productTableContent">
              {formatCondition(levels, "Levels")}
            </AssembleTypography>
          </Grid>
          <Grid size={2}>
            <AssembleTypography variant="productTableContent">
              {formatCondition(compComponents, "Compensation")}
            </AssembleTypography>
          </Grid>
          <Grid size={2}>
            <Box
              className={cx(
                classes.buttonContainer,
                "location-adjustment-row-actions"
              )}
            >
              <Tooltip title="Edit Adjustment" placement="top">
                <IconButton
                  size="small"
                  onClick={() =>
                    openAdjustmentEditor({
                      mode: "edit",
                      existingLocationAdjustment: locationAdjustment,
                    })
                  }
                >
                  <EditIcon
                    color={GRAY_4}
                    hoverColor={PURPLE_2}
                    height={"24px"}
                    width={"24px"}
                  />
                </IconButton>
              </Tooltip>
              <Box m={0.5} />
              <Tooltip title="Duplicate Adjustment" placement="top">
                <IconButton
                  size="small"
                  onClick={() =>
                    openAdjustmentEditor({
                      mode: "duplicate",
                      existingLocationAdjustment: locationAdjustment,
                    })
                  }
                >
                  <CopyIcon
                    color={GRAY_4}
                    hoverColor={PURPLE_2}
                    height={"24px"}
                    width={"24px"}
                  />
                </IconButton>
              </Tooltip>
              <Box m={0.5} />
              <DeleteButton
                buttonType="icon"
                size="small"
                objectName="Adjustment"
                onDelete={deleteLocationAdjustment}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Draggable>
  );
}

export function SourceLocationAdjustmentRow({
  locationAdjustment,
}: {
  locationAdjustment: ComputedLocationAdjustment;
}): JSX.Element {
  const classes = useStyles();

  const { adjustmentValue, departments, ladders, levels, compComponents } =
    locationAdjustment;

  return (
    <Grid container className={classes.container}>
      <Grid size={2}>
        <Box display="flex" justifyContent="flex-start" alignItems="center">
          <Box mr={6} />
          <AssembleTypography
            variant="productTableContent"
            className={classes.adjustmentValue}
            style={{ color: getAdjustmentColor(adjustmentValue) }}
          >
            {formatAdjustmentValue(adjustmentValue)}
          </AssembleTypography>
        </Box>
      </Grid>
      <Grid size={2}>
        <AssembleTypography variant="productTableContent">
          {formatCondition(
            departments.map((d) => d.name),
            "Departments"
          )}
        </AssembleTypography>
      </Grid>
      <Grid size={2}>
        <AssembleTypography variant="productTableContent">
          {formatCondition(
            ladders.map((l) => l.name),
            "Ladders"
          )}
        </AssembleTypography>
      </Grid>
      <Grid size={2}>
        <AssembleTypography variant="productTableContent">
          {formatCondition(levels, "Levels")}
        </AssembleTypography>
      </Grid>
      <Grid size={2}>
        <AssembleTypography variant="productTableContent">
          {formatCondition(compComponents, "Compensation")}
        </AssembleTypography>
      </Grid>
    </Grid>
  );
}

function formatAdjustmentValue(adjustmentValue: number): string {
  return adjustmentValue > 0 ? `+${adjustmentValue}%` : `${adjustmentValue}%`;
}

function formatCondition(
  ids: number[] | string[],
  type: "Departments" | "Ladders" | "Levels" | "Compensation"
): string {
  if (ids.length === 0) return `All ${type}`;
  return ids.join(", ");
}
